// src/services/authService.js
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";

class AuthService {
  constructor() {
    this.isRefreshing = false;
  }

  // ── AUTH ─────────────────────────────────
  async signin(username, password) {
    try {
      const res = await fetch(`${API_BASE}/api/auth/token/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      if (!res.ok) {
        const err = await res.json();
        return { success: false, error: err.detail || "Invalid credentials" };
      }
      const data = await res.json();
      localStorage.setItem("accessToken", data.access);
      localStorage.setItem("refreshToken", data.refresh);
      await this.fetchUserInfo();
      return { success: true };
    } catch (e) {
      return { success: false, error: "Failed to connect to server" };
    }
  }

  async refreshToken() {
    const refresh = localStorage.getItem("refreshToken");
    if (!refresh) { this.logout(); return null; }
    try {
      const res = await fetch(`${API_BASE}/api/auth/token/refresh/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh }),
      });
      if (!res.ok) { this.logout(); return null; }
      const data = await res.json();
      localStorage.setItem("accessToken", data.access);
      if (data.refresh) localStorage.setItem("refreshToken", data.refresh);
      return data.access;
    } catch {
      this.logout();
      return null;
    }
  }

  async fetchUserInfo() {
    try {
      const res = await fetch(`${API_BASE}/api/auth/me/`, { headers: this.getAuthHeaders() });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      localStorage.setItem("user", JSON.stringify(data));
      return data;
    } catch {
      return null;
    }
  }

  // ── SPORTS & TEAMS ──────────────────────
  async getSports() { return this._get("/api/sports/"); }
  async getTeams() { return this._get("/api/teams/"); }
  async createTeam(data) { return this._post("/api/teams/", data); }

  // ── USERS ───────────────────────────────
  async getUsers() { return this._get("/api/users/"); }
  async createUser(data) { return this._post("/api/users/create/", data); }

  // ── GRANTS (team-scoped + expirable) ────
  async getUserGrants(userId) { return this._get(`/api/users/${userId}/grants/`); }

  async grantPermissions(userId, grants) {
    // grants = [{permission_id, team_id, expires_at, note}, ...]
    return this._post(`/api/users/${userId}/grants/`, { grants });
  }

  async revokeGrants(userId, grantIds) {
    return this._delete(`/api/users/${userId}/grants/`, { grant_ids: grantIds });
  }

  // ── USER GROUPS ─────────────────────────
  async getUserGroups(userId) { return this._get(`/api/users/${userId}/groups/`); }

  async setUserGroups(userId, groupIds) {
    return this._post(`/api/users/${userId}/groups/`, { group_ids: groupIds });
  }

  // ── PERMISSIONS CRUD ────────────────────
  async getPermissions() { return this._get("/api/permissions/"); }
  async createPermission(data) { return this._post("/api/permissions/", data); }
  async deletePermission(id) { return this._delete(`/api/permissions/${id}/`); }

  // ── GROUPS CRUD ─────────────────────────
  async getGroups() { return this._get("/api/groups/"); }
  async createGroup(data) { return this._post("/api/groups/", data); }
  async updateGroup(id, data) { return this._put(`/api/groups/${id}/`, data); }
  async deleteGroup(id) { return this._delete(`/api/groups/${id}/`); }

  // ── HELPERS ──────────────────────────────
  getAccessToken() { return localStorage.getItem("accessToken"); }
  getUser() { const u = localStorage.getItem("user"); return u ? JSON.parse(u) : null; }
  isAuthenticated() { return !!this.getAccessToken(); }

  getAuthHeaders() {
    const token = this.getAccessToken();
    return {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }

  logout() {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    window.location.href = "/login";
  }

  // ── FETCH WRAPPERS ──────────────────────
  async _get(path) {
    try {
      const res = await fetch(`${API_BASE}${path}`, { headers: this.getAuthHeaders() });
      if (res.status === 401) {
        const refreshed = await this.refreshToken();
        if (!refreshed) return { success: false, error: "Session expired" };
        const retry = await fetch(`${API_BASE}${path}`, { headers: this.getAuthHeaders() });
        if (!retry.ok) return { success: false, error: `Failed (${retry.status})` };
        return { success: true, data: await retry.json() };
      }
      if (!res.ok) return { success: false, error: `Failed (${res.status})` };
      return { success: true, data: await res.json() };
    } catch (e) {
      return { success: false, error: e.message };
    }
  }

  async _post(path, body) {
    try {
      const res = await fetch(`${API_BASE}${path}`, {
        method: "POST", headers: this.getAuthHeaders(), body: JSON.stringify(body),
      });
      if (res.status === 401) {
        const refreshed = await this.refreshToken();
        if (!refreshed) return { success: false, error: "Session expired" };
        const retry = await fetch(`${API_BASE}${path}`, {
          method: "POST", headers: this.getAuthHeaders(), body: JSON.stringify(body),
        });
        const data = await retry.json();
        return retry.ok ? { success: true, data } : { success: false, error: data };
      }
      const data = await res.json();
      return res.ok ? { success: true, data } : { success: false, error: data };
    } catch (e) {
      return { success: false, error: e.message };
    }
  }

  async _put(path, body) {
    try {
      const res = await fetch(`${API_BASE}${path}`, {
        method: "PUT", headers: this.getAuthHeaders(), body: JSON.stringify(body),
      });
      const data = await res.json();
      return res.ok ? { success: true, data } : { success: false, error: data };
    } catch (e) {
      return { success: false, error: e.message };
    }
  }

  async _delete(path, body = null) {
    try {
      const opts = { method: "DELETE", headers: this.getAuthHeaders() };
      if (body) opts.body = JSON.stringify(body);
      const res = await fetch(`${API_BASE}${path}`, opts);
      if (res.status === 204) return { success: true };
      const data = await res.json().catch(() => null);
      return res.ok ? { success: true, data } : { success: false, error: data };
    } catch (e) {
      return { success: false, error: e.message };
    }
  }
}

const authService = new AuthService();
export default authService;
